/**
 * AI Media Buying Advisor Tab — TJ Command Center Phase 3
 *
 * Features:
 * - Run on-demand analyses (daily brief, campaign health, budget allocation, etc.)
 * - View analysis reports with markdown rendering
 * - Manage CPL targets per lead type
 * - Risk alerts banner
 * - Report history
 */

import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import {
  useAIAdvisorReports,
  useRunAnalysis,
  useCPLTargets,
  useUpsertCPLTarget,
  ANALYSIS_TYPE_META,
  type AnalysisType,
  type AIAdvisorReport,
} from "@/hooks/useAIAdvisor";
import { useAdCampaigns } from "@/hooks/useAdPerformance";
import {
  Loader2,
  Brain,
  Play,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Sun,
  Activity,
  PieChart,
  GitBranch,
  ShieldAlert,
  Target,
  Settings2,
  DollarSign,
  Save,
  Calculator,
} from "lucide-react";

// ─── Lead type metadata ────────────────────────────────────

const LEAD_TYPES = [
  { id: "life_fex", label: "Final Expense", vertical: "life_insurance" },
  { id: "life_iul", label: "IUL", vertical: "life_insurance" },
  { id: "life_mp", label: "Mortgage Protection", vertical: "life_insurance" },
  { id: "mortgage_purchase", label: "Purchase", vertical: "mortgage" },
  { id: "mortgage_refi", label: "Refinance", vertical: "mortgage" },
  { id: "mortgage_heloc", label: "HELOC", vertical: "mortgage" },
  { id: "mortgage_reverse", label: "Reverse Mortgage", vertical: "mortgage" },
];

// ─── Icon mapping ──────────────────────────────────────────

const ANALYSIS_ICONS: Record<string, React.ReactNode> = {
  daily_brief: <Sun className="h-4 w-4" />,
  campaign_health: <Activity className="h-4 w-4" />,
  budget_allocation: <PieChart className="h-4 w-4" />,
  anomaly_check: <AlertTriangle className="h-4 w-4" />,
  ab_test: <GitBranch className="h-4 w-4" />,
  fulfillment_risk: <ShieldAlert className="h-4 w-4" />,
  budget_simulator: <Calculator className="h-4 w-4" />,
};

// ─── Markdown renderer ──────────────────────────────────────

function renderMarkdownTable(block: string): string {
  const rows = block.trim().split("\n").filter((r) => r.trim());
  if (rows.length < 2) return block;

  // Check if second row is a separator (|---|---|)
  const isSeparator = (row: string) => /^\|[\s\-:|]+\|$/.test(row.trim());
  const separatorIdx = rows.findIndex(isSeparator);
  if (separatorIdx < 0) return block;

  const parseRow = (row: string) =>
    row.split("|").slice(1, -1).map((c) => c.trim());

  const headerCells = parseRow(rows[separatorIdx - 1] || rows[0]);
  const dataRows = rows.slice(separatorIdx + 1);

  let html = '<div class="overflow-x-auto my-3"><table class="w-full text-sm border-collapse">';
  html += "<thead><tr>";
  for (const cell of headerCells) {
    html += `<th class="text-left font-semibold px-3 py-2 border-b-2 border-border bg-muted/40 text-xs uppercase tracking-wide">${cell}</th>`;
  }
  html += "</tr></thead><tbody>";
  for (const row of dataRows) {
    if (isSeparator(row)) continue;
    const cells = parseRow(row);
    html += '<tr class="border-b border-border/50 hover:bg-muted/20">';
    for (let i = 0; i < headerCells.length; i++) {
      const val = cells[i] ?? "";
      html += `<td class="px-3 py-2 text-sm">${val}</td>`;
    }
    html += "</tr>";
  }
  html += "</tbody></table></div>";
  return html;
}

function MarkdownContent({ content }: { content: string }) {
  const html = useMemo(() => {
    // Split into lines for block-level processing
    const lines = content.split("\n");
    const outputParts: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Detect table blocks (lines starting with |)
      if (line.trim().startsWith("|")) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith("|")) {
          tableLines.push(lines[i]);
          i++;
        }
        outputParts.push(renderMarkdownTable(tableLines.join("\n")));
        continue;
      }

      // Process single line
      let processed = line
        // Escape HTML (but preserve our generated HTML)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      // Headers
      if (/^### (.+)$/.test(processed)) {
        processed = processed.replace(/^### (.+)$/, '<h4 class="text-sm font-semibold mt-4 mb-1">$1</h4>');
      } else if (/^## (.+)$/.test(processed)) {
        processed = processed.replace(/^## (.+)$/, '<h3 class="text-base font-bold mt-5 mb-2 text-[#1a1a2e] border-b border-border/30 pb-1">$1</h3>');
      } else if (/^# (.+)$/.test(processed)) {
        processed = processed.replace(/^# (.+)$/, '<h2 class="text-lg font-bold mt-6 mb-2 text-[#1a1a2e]">$1</h2>');
      }
      // Horizontal rules
      else if (/^---+$/.test(processed.trim())) {
        processed = '<hr class="my-3 border-border" />';
      }
      // Numbered list items
      else if (/^\d+\.\s/.test(processed)) {
        processed = processed.replace(/^(\d+)\.\s(.+)$/, '<div class="flex gap-2 ml-2 mb-1"><span class="text-muted-foreground font-mono text-xs mt-0.5 shrink-0">$1.</span><span class="text-sm">$2</span></div>');
      }
      // Bullet list items
      else if (/^[-*]\s/.test(processed.trim())) {
        processed = processed.replace(/^\s*[-*]\s(.+)$/, '<div class="flex gap-2 ml-2 mb-1"><span class="text-muted-foreground mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-current inline-block"></span><span class="text-sm">$1</span></div>');
      }
      // Empty line = paragraph break
      else if (processed.trim() === "") {
        processed = '<div class="h-2"></div>';
      }
      // Regular text
      else {
        processed = `<p class="text-sm leading-relaxed mb-1">${processed}</p>`;
      }

      // Inline formatting (applied to all lines)
      processed = processed
        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-xs font-mono">$1</code>')
        // Urgency tags
        .replace(/\[CRITICAL\]/g, '<span class="inline-flex items-center gap-1 text-red-700 font-semibold"><span class="w-2 h-2 rounded-full bg-red-500 inline-block"></span>CRITICAL</span>')
        .replace(/\[WARNING\]/g, '<span class="inline-flex items-center gap-1 text-yellow-700 font-semibold"><span class="w-2 h-2 rounded-full bg-yellow-500 inline-block"></span>WARNING</span>')
        .replace(/\[HEALTHY\]/g, '<span class="inline-flex items-center gap-1 text-green-700 font-semibold"><span class="w-2 h-2 rounded-full bg-green-500 inline-block"></span>HEALTHY</span>')
        // Emoji urgency badges (fallback if model uses emojis)
        .replace(/🔴/g, '<span class="inline-block w-2.5 h-2.5 rounded-full bg-red-500 mr-1"></span>')
        .replace(/🟡/g, '<span class="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500 mr-1"></span>')
        .replace(/🟢/g, '<span class="inline-block w-2.5 h-2.5 rounded-full bg-green-500 mr-1"></span>');

      outputParts.push(processed);
      i++;
    }

    return outputParts.join("\n");
  }, [content]);

  return (
    <div
      className="max-w-none"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ─── Risk Alerts Banner ────────────────────────────────────

function RiskAlertsBanner({ reports }: { reports: AIAdvisorReport[] }) {
  // Collect critical/warning alerts from recent reports (last 24h)
  const recentAlerts = useMemo(() => {
    const cutoff = new Date();
    cutoff.setHours(cutoff.getHours() - 24);

    return reports
      .filter((r) => r.status === "completed" && new Date(r.created_at) > cutoff)
      .flatMap((r) => {
        const alerts = Array.isArray(r.risk_alerts) ? r.risk_alerts : [];
        return alerts
          .filter((a: any) => a.severity === "critical" || a.severity === "warning")
          .map((a: any) => ({ ...a, reportType: r.type, reportDate: r.created_at }));
      });
  }, [reports]);

  if (recentAlerts.length === 0) return null;

  const criticalCount = recentAlerts.filter((a) => a.severity === "critical").length;
  const warningCount = recentAlerts.filter((a) => a.severity === "warning").length;

  return (
    <div
      className={`rounded-lg border p-4 ${
        criticalCount > 0
          ? "bg-red-50 border-red-200"
          : "bg-yellow-50 border-yellow-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle
          className={`h-4 w-4 ${criticalCount > 0 ? "text-red-600" : "text-yellow-600"}`}
        />
        <span className="font-semibold text-sm">
          {criticalCount > 0 ? `${criticalCount} Critical` : ""}
          {criticalCount > 0 && warningCount > 0 ? " + " : ""}
          {warningCount > 0 ? `${warningCount} Warning` : ""} Alert
          {criticalCount + warningCount > 1 ? "s" : ""}
        </span>
      </div>
      <div className="space-y-1">
        {recentAlerts.slice(0, 5).map((alert, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <span
              className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                alert.severity === "critical" ? "bg-red-500" : "bg-yellow-500"
              }`}
            />
            <span>
              <strong>{alert.title}</strong> — {alert.description}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CPL Targets Manager ───────────────────────────────────

function CPLTargetsManager() {
  const { data: targets = [], isLoading } = useCPLTargets();
  const upsertTarget = useUpsertCPLTarget();
  const { toast } = useToast();

  const [editValues, setEditValues] = useState<
    Record<string, { low: string; high: string; threshold: string }>
  >({});

  const getValues = (leadTypeId: string) => {
    if (editValues[leadTypeId]) return editValues[leadTypeId];
    const existing = targets.find((t) => t.lead_type_id === leadTypeId);
    return {
      low: existing?.target_cpl_low?.toString() ?? "",
      high: existing?.target_cpl_high?.toString() ?? "",
      threshold: existing?.alert_threshold_pct?.toString() ?? "30",
    };
  };

  const handleChange = (leadTypeId: string, field: "low" | "high" | "threshold", value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [leadTypeId]: { ...getValues(leadTypeId), [field]: value },
    }));
  };

  const handleSave = async (leadTypeId: string) => {
    const vals = getValues(leadTypeId);
    const lt = LEAD_TYPES.find((l) => l.id === leadTypeId);
    if (!lt) return;

    const low = parseFloat(vals.low);
    const high = parseFloat(vals.high);
    const threshold = parseFloat(vals.threshold);

    if (isNaN(low) || isNaN(high) || low <= 0 || high <= 0 || low > high) {
      toast({
        title: "Invalid values",
        description: "CPL Low must be less than High, both positive.",
        variant: "destructive",
      });
      return;
    }

    try {
      await upsertTarget.mutateAsync({
        lead_type_id: leadTypeId,
        vertical: lt.vertical,
        target_cpl_low: low,
        target_cpl_high: high,
        alert_threshold_pct: isNaN(threshold) ? 30 : threshold,
        notes: null,
        updated_by: null,
      });
      setEditValues((prev) => {
        const next = { ...prev };
        delete next[leadTypeId];
        return next;
      });
      toast({ title: "Saved", description: `CPL target for ${lt.label} updated.` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-[#D4A843]" />
          <CardTitle className="text-sm font-medium">CPL Targets</CardTitle>
        </div>
        <CardDescription className="text-xs">
          Set acceptable CPL ranges per lead type. The advisor uses these targets to evaluate performance.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Life Insurance */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Life Insurance
            </p>
            <div className="space-y-2">
              {LEAD_TYPES.filter((lt) => lt.vertical === "life_insurance").map((lt) => {
                const vals = getValues(lt.id);
                const existing = targets.find((t) => t.lead_type_id === lt.id);
                const hasChanges = editValues[lt.id] !== undefined;

                return (
                  <div key={lt.id} className="flex items-center gap-2">
                    <span className="text-sm w-[140px] shrink-0">{lt.label}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">$</span>
                      <Input
                        type="number"
                        placeholder="Low"
                        value={vals.low}
                        onChange={(e) => handleChange(lt.id, "low", e.target.value)}
                        className="w-20 h-8 text-sm"
                      />
                      <span className="text-xs text-muted-foreground">—</span>
                      <span className="text-xs text-muted-foreground">$</span>
                      <Input
                        type="number"
                        placeholder="High"
                        value={vals.high}
                        onChange={(e) => handleChange(lt.id, "high", e.target.value)}
                        className="w-20 h-8 text-sm"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      disabled={upsertTarget.isPending || (!hasChanges && !!existing)}
                      onClick={() => handleSave(lt.id)}
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                    {existing && !hasChanges && (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Mortgage */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Mortgage
            </p>
            <div className="space-y-2">
              {LEAD_TYPES.filter((lt) => lt.vertical === "mortgage").map((lt) => {
                const vals = getValues(lt.id);
                const existing = targets.find((t) => t.lead_type_id === lt.id);
                const hasChanges = editValues[lt.id] !== undefined;

                return (
                  <div key={lt.id} className="flex items-center gap-2">
                    <span className="text-sm w-[140px] shrink-0">{lt.label}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">$</span>
                      <Input
                        type="number"
                        placeholder="Low"
                        value={vals.low}
                        onChange={(e) => handleChange(lt.id, "low", e.target.value)}
                        className="w-20 h-8 text-sm"
                      />
                      <span className="text-xs text-muted-foreground">—</span>
                      <span className="text-xs text-muted-foreground">$</span>
                      <Input
                        type="number"
                        placeholder="High"
                        value={vals.high}
                        onChange={(e) => handleChange(lt.id, "high", e.target.value)}
                        className="w-20 h-8 text-sm"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      disabled={upsertTarget.isPending || (!hasChanges && !!existing)}
                      onClick={() => handleSave(lt.id)}
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                    {existing && !hasChanges && (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Report Card ───────────────────────────────────────────

function ReportCard({
  report,
  isSelected,
  onSelect,
}: {
  report: AIAdvisorReport;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const meta = ANALYSIS_TYPE_META[report.type as AnalysisType];
  const date = new Date(report.created_at);
  const timeAgo = getTimeAgo(date);

  const structured = report.structured_output as any;
  const healthScore = structured?.health_score;

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border p-3 transition-colors hover:bg-muted/50 ${
        isSelected ? "border-[#D4A843] bg-[#D4A843]/5" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {ANALYSIS_ICONS[report.type] || <Brain className="h-4 w-4" />}
          <div>
            <p className="text-sm font-medium">{meta?.label ?? report.type}</p>
            <p className="text-xs text-muted-foreground">{timeAgo}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {healthScore !== undefined && (
            <span
              className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                healthScore >= 70
                  ? "bg-green-100 text-green-700"
                  : healthScore >= 40
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {healthScore}
            </span>
          )}
          <StatusIcon status={report.status} />
        </div>
      </div>
      {report.status === "completed" && structured?.summary && (
        <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
          {structured.summary}
        </p>
      )}
    </button>
  );
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case "generating":
      return <Loader2 className="h-4 w-4 animate-spin text-[#D4A843]" />;
    case "error":
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />;
  }
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
}

// ─── Main Component ────────────────────────────────────────

export function AIAdvisorTab() {
  const { data: reports = [], isLoading: reportsLoading } = useAIAdvisorReports();
  const runAnalysis = useRunAnalysis();
  const campaigns = useAdCampaigns();
  const { toast } = useToast();

  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState<AnalysisType>("daily_brief");
  const [showSettings, setShowSettings] = useState(false);
  const [compareIds, setCompareIds] = useState<string[]>([]);

  const selectedReport = reports.find((r) => r.id === selectedReportId) ?? null;

  const handleRunAnalysis = async () => {
    try {
      const params: any = {};

      if (selectedAnalysisType === "ab_test") {
        if (compareIds.length < 2) {
          toast({
            title: "Select campaigns",
            description: "A/B Test requires at least 2 campaigns to compare.",
            variant: "destructive",
          });
          return;
        }
        params.compare_ids = compareIds;
      }

      const result = await runAnalysis.mutateAsync({
        type: selectedAnalysisType,
        params: Object.keys(params).length > 0 ? params : undefined,
      });

      setSelectedReportId(result.id);
      toast({ title: "Analysis complete", description: `${ANALYSIS_TYPE_META[selectedAnalysisType].label} generated successfully.` });
    } catch (err: any) {
      toast({
        title: "Analysis error",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Risk Alerts Banner */}
      <RiskAlertsBanner reports={reports} />

      {/* Action Bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-[#D4A843]" />
              <span className="font-semibold text-sm">AI Advisor</span>
            </div>
            <div className="flex items-center gap-2 flex-1">
              <Select
                value={selectedAnalysisType}
                onValueChange={(v) => setSelectedAnalysisType(v as AnalysisType)}
              >
                <SelectTrigger className="w-[200px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ANALYSIS_TYPE_META).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {ANALYSIS_ICONS[key]}
                        <span>{meta.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handleRunAnalysis}
                disabled={runAnalysis.isPending}
                className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90"
                size="sm"
              >
                {runAnalysis.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Play className="h-4 w-4 mr-1" />
                )}
                Analyze
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className="shrink-0"
            >
              <Settings2 className="h-4 w-4 mr-1" />
              CPL Targets
            </Button>
          </div>

          {/* A/B Test: campaign selector */}
          {selectedAnalysisType === "ab_test" && (
            <div className="mt-3 pt-3 border-t">
              <Label className="text-xs text-muted-foreground mb-2 block">
                Select 2+ campaigns to compare:
              </Label>
              <div className="flex flex-wrap gap-2">
                {campaigns.data?.map((c) => {
                  const isSelected = compareIds.includes(c.meta_campaign_id);
                  return (
                    <Button
                      key={c.meta_campaign_id}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className={`text-xs h-7 ${isSelected ? "bg-[#D4A843] hover:bg-[#D4A843]/90" : ""}`}
                      onClick={() => {
                        setCompareIds((prev) =>
                          isSelected
                            ? prev.filter((id) => id !== c.meta_campaign_id)
                            : [...prev, c.meta_campaign_id]
                        );
                      }}
                    >
                      {c.meta_campaign_name}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Analysis type description */}
          <p className="text-xs text-muted-foreground mt-2">
            {ANALYSIS_TYPE_META[selectedAnalysisType].description}
          </p>
        </CardContent>
      </Card>

      {/* CPL Targets (collapsible) */}
      {showSettings && <CPLTargetsManager />}

      {/* Main Content: Reports History + Selected Report */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Report History (left) */}
        <div className="lg:col-span-1 space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground px-1">
            History ({reports.length})
          </h3>

          {reportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                <Brain className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No analyses yet. Click "Analyze" to get started.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {reports.map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  isSelected={selectedReportId === report.id}
                  onSelect={() => setSelectedReportId(report.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Selected Report (right) */}
        <div className="lg:col-span-2">
          {runAnalysis.isPending ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#D4A843] mx-auto mb-3" />
                <p className="text-sm font-medium">Analyzing data...</p>
                <p className="text-xs text-muted-foreground mt-1">
                  The AI Advisor is processing your performance data.
                  <br />
                  This may take 15-30 seconds.
                </p>
              </CardContent>
            </Card>
          ) : selectedReport ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {ANALYSIS_ICONS[selectedReport.type] || <Brain className="h-4 w-4" />}
                    <CardTitle className="text-sm font-medium">
                      {ANALYSIS_TYPE_META[selectedReport.type as AnalysisType]?.label ?? selectedReport.type}
                    </CardTitle>
                    <StatusIcon status={selectedReport.status} />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {selectedReport.tokens_used && (
                      <span>{selectedReport.tokens_used.toLocaleString()} tokens</span>
                    )}
                    {selectedReport.cost_usd !== null && (
                      <span>
                        <DollarSign className="h-3 w-3 inline" />
                        {selectedReport.cost_usd.toFixed(4)}
                      </span>
                    )}
                    <span>
                      {new Date(selectedReport.created_at).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {selectedReport.status === "error" ? (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800">
                    <strong>Analysis error:</strong> {selectedReport.analysis}
                  </div>
                ) : selectedReport.analysis ? (
                  <div className="space-y-4">
                    <MarkdownContent content={selectedReport.analysis} />

                    {/* Recommendations */}
                    {Array.isArray(selectedReport.recommendations) &&
                      selectedReport.recommendations.length > 0 && (
                        <Collapsible defaultOpen>
                          <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium hover:text-[#D4A843] transition-colors">
                            <ChevronDown className="h-4 w-4" />
                            Recommendations ({selectedReport.recommendations.length})
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2">
                            <div className="space-y-2">
                              {selectedReport.recommendations.map((rec: any, i: number) => (
                                <div
                                  key={i}
                                  className="border rounded-md p-3 bg-muted/30"
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge
                                      variant="outline"
                                      className={
                                        rec.effort === "low"
                                          ? "border-green-300 text-green-700"
                                          : rec.effort === "high"
                                          ? "border-red-300 text-red-700"
                                          : "border-yellow-300 text-yellow-700"
                                      }
                                    >
                                      {rec.effort ?? "medium"}
                                    </Badge>
                                    <span className="text-sm font-medium">
                                      #{rec.priority ?? i + 1}. {rec.action}
                                    </span>
                                  </div>
                                  {rec.rationale && (
                                    <p className="text-xs text-muted-foreground">{rec.rationale}</p>
                                  )}
                                  {rec.expected_impact && (
                                    <p className="text-xs text-green-700 mt-1">
                                      Expected impact: {rec.expected_impact}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Awaiting result...</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <Brain className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                  Select an analysis from history or run a new one.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
